<script setup lang="ts">
import Translator from "@/utils/common/Translator";
import {computed, onBeforeMount, ref, watch} from "vue";
import {
  useDarkMode,
  useDensityMode,
  useHorizontalMode,
  useNamaFlagsStore,
  useQuadLayout,
  useSidebarMenuIconColors,
  useToolbarLabels
} from "@/stores/nama-flags-store.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {useThemeStore} from "@/stores/ThemeDataStore.ts";
import router from "@/router";
import RouteNames from "@/router/RouteNames.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import PageTitle from "@/components/settings/PageTitle.vue";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import QuestionFieldsEditor from "@/components/questionFields/QuestionFieldsEditor.vue";
import AddRecordIcon from "@/assets/editViewIcons/AddRecordIcon.vue";
import {QuestionFieldsWithValues, UserMenuItem} from "@/utils/presenters/interfaces.ts";
import NotificationUtils from "@/utils/broadcast/NotificationUtils.ts";
import DTOVueGUITheme = namaTypes.DTOVueGUITheme;
import QuestionField = namaTypes.QuestionField;

const {darkMode} = useDarkMode();
const {quadLayout} = useQuadLayout();
const {densityMode} = useDensityMode();
const {showToolbarLabels} = useToolbarLabels()
const {horizontalMode} = useHorizontalMode();
const {showSidebarMenuIconColors} = useSidebarMenuIconColors();
const themeList = ref<DTOVueGUITheme[]>([]);
const currentTheme = ref(localStorage.getItem('theme-code'));

const themeMenu = computed<UserMenuItem[]>(() => {
  if (!themeList.value)
    return [];
  let menu: UserMenuItem[] = []
  for (let theme of themeList.value)
    menu.push({
      theme: theme,
      handler: () => applyTheme(theme.code)
    })
  return menu;
});


let samplePresenter: QuestionFieldsPresenter = new QuestionFieldsPresenter();
let sampleQuestionFields: QuestionField[] = QuestionFieldsPresenter.createSampleQuestionFields()
let soundPresenter: QuestionFieldsPresenter = new QuestionFieldsPresenter();
let soundQuestionFieldsWithValues: QuestionFieldsWithValues = NotificationUtils.createSoundQuestionFields();

onBeforeMount(async () => {
  await Promise.all([
    samplePresenter.edit(sampleQuestionFields, []),
    soundPresenter.edit(soundQuestionFieldsWithValues.questionFields, soundQuestionFieldsWithValues.defaultValues)
  ]);
  let list: DTOVueGUITheme[] | undefined = await useThemeStore().fetchListThemes();
  if (list)
    themeList.value = list;
})

watch(
  () => [
    soundPresenter.fetchRecord()?.notificationsSound,
    soundPresenter.fetchRecord()?.approvalsSound,
    soundPresenter.fetchRecord()?.messagesSound,
    soundPresenter.fetchRecord()?.taskEndedSound,
  ],
  ([notifications, approvals, messages, taskEnded]) => {
    const store = useNamaFlagsStore();
    if (notifications && notifications !== store.notificationsSound)
      store.updateNotificationsSound(notifications);
    if (approvals && approvals !== store.approvalsSound)
      store.updateApprovalsSound(approvals);
    if (messages && messages !== store.messagesSound)
      store.updateMessagesSound(messages);
    if (taskEnded && taskEnded !== store.taskEndedSound)
      store.updateTaskEndedSound(taskEnded);
  }
)

async function applyTheme(code: string | undefined) {
  await useThemeStore().fetchAndApplyTheme(code);
  currentTheme.value = localStorage.getItem('theme-code');
}

const fieldsPreferences = ref<UserMenuItem[]>([
  {
    vModel: horizontalMode,
    titleId: "horizontalMode",
    defaultIcon: "nm-horizontal"
  },
  {
    vModel: quadLayout,
    titleId: "quadLayout",
    defaultIcon: "nm-quad-layout"
  },
  {
    vModel: showToolbarLabels,
    titleId: "toolbarLabels",
    defaultIcon: "visibility"
  }
]);

const extraPreferences = ref<UserMenuItem[]>([
  {
    titleId: "densityMode",
    vModelStr: densityMode,
    defaultIcon: "nm-compact",
    hasDivider: true
  }, {
    titleId: "darkMode",
    vModel: darkMode,
    defaultIcon: "nm-dark-mode",
    hasDivider: true
  },
  {
    titleId: "showMenuIconColors",
    vModel: showSidebarMenuIconColors,
    defaultIcon: "nm-color-palette",
    hasDivider: true
  }
]);

const densityOptions = [
  {label: Translator.translateId('density.normal'), value: 'Normal'},
  {label: Translator.translateId('Compact'), value: 'Compact'},
  {label: Translator.translateId('Ultra'), value: 'Ultra'}
];

function MenuItemHandler(item: UserMenuItem) {
  if (item.hasOwnProperty('vModel')) {
    item.vModel = !item.vModel
  } else if (item.handler) {
    item.handler();
  }
}

function themeHandler(item: UserMenuItem | undefined) {
  if (item && item.handler)
    item.handler()
}

async function goToGuiTheme() {
  await router.push({
    name: RouteNames.EDIT_VIEW_ROUTE,
    query: {
      [PlaceTokens.PRM_ENTITY_TYPE]: BSCEntities.GUITheme,
      [PlaceTokens.PRM_VIEW]: ViewMode.NEW,
    }
  });
}

</script>

<template>
  <div>
    <div>
      <div class="tw-px-6 tw-pt-1 tw-w-full">
        <PageTitle title="screensPreferences" icon="nm-screen-modifer" class="tw-pl-0"/>
        <div class="tw-flex tw-flex-wrap tw-gap-x-[4%] tw-items-center tw-px-8 tw-py-2">
          <template v-for="item in fieldsPreferences">
            <div class="tw-select-none tw-flex tw-justify-between tw-gap-1.5 tw-items-center !tw-py-1.5 tw-w-fit"
                 @click.capture="MenuItemHandler(item)">
              <div class="tw-w-full tw-flex tw-flex-row">
                <NamaIcon :default-icon="item.defaultIcon" class="tw-size-5"/>
                <span v-text="Translator.translateId(item.titleId)"
                      class="tw-px-3 tw-flex-grow tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark"/>
                <q-toggle :id="item.titleId" v-if="item.hasOwnProperty('vModel')" color="blue" :model-value="item.vModel" dense/>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="tw-w-full tw-justify-items-center">
      <div class="tw-w-[90%] tw-h-[430px]">
        <PageTitle title="screenPreview" class="tw-p-0 tw-pb-2"/>
        <div class="tw-bg-namaBackground-dim-light dark:tw-bg-namaBackground-dim-dark tw-px-8 tw-py-2 tw-rounded-t-lg">
          <div class="tw-flex tw-gap-2 tw-justify-between">
            <div class="tw-flex tw-gap-2 tw-flex-nowrap tw-overflow-x-auto">
              <a
                class="hover:tw-bg-gray-200 active:tw-bg-gray-300 tw-gap-1 tw-flex tw-flex-col tw-items-center
            tw-p-2 tw-rounded-lg tw-cursor-pointer  dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600"
                :title="Translator.translateId('add')">
                <AddRecordIcon class="tw-h-6"/>
                <span
                  v-text="Translator.translateId('add')"
                  class="tw-text-namaFontColor-light/70 dark:tw-text-white/70 tw-text-xs tw-select-none tw-capitalize tw-whitespace-nowrap"
                  v-if="showToolbarLabels"/>
              </a>
              <a
                class="hover:tw-bg-gray-200 active:tw-bg-gray-300 tw-gap-1 tw-flex tw-flex-col tw-items-center
            tw-p-2 tw-rounded-lg tw-cursor-pointer  dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600"
                :title="Translator.translateId('delete')">
                <NamaIcon default-icon="nm-delete" class="tw-size-6 active-primary"/>
                <span
                  v-text="Translator.translateId('delete')"
                  class="tw-text-namaFontColor-light/70 dark:tw-text-white/70 tw-text-xs tw-select-none tw-capitalize tw-whitespace-nowrap"
                  v-if="showToolbarLabels"/>
              </a>
            </div>
          </div>
        </div>
        <q-card
          class="tw-w-full tw-h-[290px] tw-bg-transparent tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark tw-rounded">
          <q-card-section
            class="tw-pb-0 tw-shrink tw-text-primary-light dark:tw-text-primary-dark tw-text-base tw-font-normal tw-h-fit tw-flex-nowrap tw-overflow-y-auto !tw-max-h-[90%]">
            <QuestionFieldsEditor :presenter="samplePresenter" :singleColumnGroup="false" :doNotApplyThemeLayouts="false"/>
          </q-card-section>
        </q-card>
      </div>
    </div>
    <div class="tw-p-6 tw-pt-1 tw-w-full">
      <PageTitle title="soundEffects" icon="audiotrack" class="tw-pl-0"/>
      <q-card-section
        class="tw-shrink tw-text-primary-light dark:tw-text-primary-dark tw-text-base tw-font-normal tw-h-fit tw-flex-nowrap tw-overflow-y-auto !tw-max-h-[90%]">
        <QuestionFieldsEditor :presenter="soundPresenter" :singleColumnGroup="false" :doNotApplyThemeLayouts="true"/>
      </q-card-section>
    </div>
    <div class="tw-p-6 tw-pt-1 tw-w-full">
      <PageTitle title="generalPreferences" icon="widgets" class="tw-pl-0"/>
      <div class="tw-flex tw-flex-wrap tw-gap-x-[4%] tw-items-center tw-px-8 tw-py-2">
        <template v-for="item in extraPreferences">
          <div class="tw-select-none tw-flex tw-justify-between tw-gap-1.5 tw-items-center !tw-py-1.5 tw-w-fit"
               @click.capture="MenuItemHandler(item)">
            <div class="tw-w-full tw-flex tw-flex-row">
              <NamaIcon :default-icon="item.defaultIcon" class="tw-size-5"/>
              <span v-text="Translator.translateId(item.titleId)"
                    class="tw-px-3 tw-flex-grow tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark"/>
              <q-option-group v-if="item.hasOwnProperty('vModelStr')" v-model="item.vModelStr" dense color="primary" inline
                              :dark="darkMode" :options="densityOptions" class="tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark"/>
              <q-toggle :id="item.titleId" v-else-if="item.hasOwnProperty('vModel')" color="primary" :model-value="item.vModel" dense/>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="tw-p-6 tw-pt-1 tw-flex  tw-flex-row tw-gap-x-[2%] tw-w-full tw-justify-between">
      <div class="tw-w-[40%] mobile:tw-w-full">
        <PageTitle title="GUIThemesList" icon="nm-theme-editor" class="tw-pl-0"/>
        <div class="tw-select-none tw-flex tw-justify-between tw-gap-1.5 tw-items-center tw-px-8 tw-py-2 tw-w-full">
          <q-card
            class="tw-w-full tw-bg-transparent tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark tw-shadow-none tw-border tw-rounded">
            <q-card-section class="tw-h-fit tw-p-2">
              <template v-for="item in themeMenu">
                <div class="tw-select-none flex justify-between tw-gap-1.5 tw-items-center !tw-p-1.5 nama-theme-list-expand hover:tw-cursor-pointer"
                     :class="{'dark:tw-bg-namaBackground-dim-dark tw-bg-namaBackground-dim-light': item.theme?.code == currentTheme}"
                     @click.capture="themeHandler(item)">
                  <div class="tw-px-0">
                    <q-icon name="circle"
                            class="tw-text-[18px] tw-rounded-full tw-ring-1 tw-ring-namaFontColor-light dark:tw-ring-namaFontColor-dark"
                            :style="{color: item.theme?.lightColorTheme?.primaryColor}"/>
                    <q-icon name="circle"
                            class="tw-text-[18px] tw-rounded-full tw-ring-1 tw-ring-namaFontColor-light dark:tw-ring-namaFontColor-dark"
                            :style="{color: item.theme?.lightColorTheme?.primaryBackgroundColor}"/>
                    <q-icon name="circle"
                            class="tw-text-[18px] tw-rounded-full tw-ring-1 tw-ring-namaFontColor-light dark:tw-ring-namaFontColor-dark"
                            :style="{color: item.theme?.darkColorTheme?.primaryColor}"/>
                    <q-icon name="circle"
                            class="tw-text-[18px] tw-rounded-full tw-ring-1 tw-ring-namaFontColor-light dark:tw-ring-namaFontColor-dark"
                            :style="{color: item.theme?.darkColorTheme?.primaryBackgroundColor}"/>
                  </div>
                  <span v-text="Translator.getName(item.theme?.name1 ?? '', item.theme?.name2 ?? '')" class="tw-flex-grow"/>
                </div>
              </template>
              <div
                class="popoverItem tw-select-none flex justify-between tw-gap-1.5 tw-items-center !tw-p-1.5 nama-theme-list-expand hover:tw-cursor-pointer"
                @click.capture="goToGuiTheme()">
                <NamaIcon default-icon="add" class="tw-size-5"/>
                <span v-text="Translator.translateId('addNew')" class="tw-flex-grow"/>
              </div>
            </q-card-section>
          </q-card>
        </div>
      </div>
    </div>
  </div>
</template>
